import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: object;
  noIndex?: boolean;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  image = 'https://res.cloudinary.com/dkaie0i1s/image/upload/v1745213182/Screenshot_2025-04-21_at_8.25.34_qy7sks.webp',
  url = 'https://www.inbalart.co.il',
  type = 'website',
  structuredData,
  noIndex = false
}) => {
  const { currentLang } = useLanguage();
  const isHebrew = currentLang === 'he';

  // Default values based on language
  const defaultTitle = isHebrew 
    ? 'ענבל ארט מייקרס - סדנאות קריקט ועיצוב דיגיטלי  | Inbal Art Makers'
    : 'Inbal Art Makers - Cricut Workshops & Digital Design  | סדנאות קריקט ';

  const defaultDescription = isHebrew
    ? 'סדנאות קריקט מקצועיות, הדרכות עיצוב דיגיטלי וקורסי תכשיטנות . למדו מהמומחית ענבל לוין בהרצליה. Professional Cricut workshops  with Inbal Levin.'
    : 'Professional Cricut workshops, digital design tutorials, and jewelry making classes . Learn from expert Inbal Levin in Herzliya. סדנאות קריקט מקצועיות  עם ענבל לוין בהרצליה.';

  const defaultKeywords = isHebrew
    ? 'סדנאות קריקט, קריקט ישראל, חיתוך דיגיטלי, תכשיטנות, עיצוב דיגיטלי, הרצליה, ענבל לוין, cricut israel, cricut workshops, digital cutting, jewelry making'
    : 'cricut israel, cricut workshops, digital cutting, jewelry making, digital design, סדנאות קריקט, קריקט ישראל, חיתוך דיגיטלי, תכשיטנות, עיצוב דיגיטלי, הרצליה, ענבל לוין';

  const finalTitle = title ? `${title} | ${isHebrew ? 'ענבל ארט מייקרס' : 'Inbal Art Makers'}` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;

  // Default structured data for organization
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": isHebrew ? "ענבל ארט מייקרס" : "Inbal Art Makers",
    "alternateName": isHebrew ? "Inbal Art Makers" : "ענבל ארט מייקרס",
    "url": url,
    "logo": image,
    "description": finalDescription,
    "foundingDate": "2020",
    "founder": {
      "@type": "Person",
      "name": isHebrew ? "ענבל לוין" : "Inbal Levin",
      "alternateName": isHebrew ? "Inbal Levin" : "ענבל לוין"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Yigal Alon St",
      "addressLocality": isHebrew ? "הרצליה" : "Herzliya",
      "addressCountry": "IL"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+972-54-7798844",
      "contactType": "customer service",
      "email": "inbal.artmakers@gmail.com"
    },
    "sameAs": [
      "https://www.facebook.com/inbalartmakers",
      "https://www.instagram.com/inbalartmakers"
    ],
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "ILS",
      "availability": "https://schema.org/InStock"
    }
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="Inbal Levin - Inbal Art Makers" />
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Language and Direction */}
      <html lang={isHebrew ? 'he' : 'en'} dir={isHebrew ? 'rtl' : 'ltr'} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={isHebrew ? "ענבל ארט מייקרס" : "Inbal Art Makers"} />
      <meta property="og:locale" content={isHebrew ? "he_IL" : "en_US"} />
      <meta property="og:locale:alternate" content={isHebrew ? "en_US" : "he_IL"} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={finalTitle} />
      <meta property="twitter:description" content={finalDescription} />
      <meta property="twitter:image" content={image} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="geo.region" content="IL" />
      <meta name="geo.placename" content={isHebrew ? "הרצליה, ישראל" : "Herzliya, Israel"} />
      <meta name="geo.position" content="32.1656;34.8447" />
      <meta name="ICBM" content="32.1656, 34.8447" />
      <meta name="language" content={isHebrew ? "Hebrew, English" : "English, Hebrew"} />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Alternate Language Versions */}
      <link rel="alternate" hrefLang="en" href={url.replace(/\?.*/, '')} />
      <link rel="alternate" hrefLang="he" href={url.replace(/\?.*/, '') + '?lang=he'} />
      <link rel="alternate" hrefLang="x-default" href={url.replace(/\?.*/, '')} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};
